'use client';

import { type RefObject, useCallback, useRef } from 'react';
import * as THREE from 'three';

import type { GameConfig } from '@/app/(football)/[team]/mini-game/_components/Section/GameSection/BlockStackingGame/types';

/**
 * Scene management hook
 * Manages Three.js scene, camera, and lighting
 */
export function useScene(containerRef: RefObject<HTMLDivElement | null>, gameConfig: GameConfig) {
  const sceneRef = useRef<THREE.Scene | undefined>(undefined);
  const cameraRef = useRef<THREE.OrthographicCamera | undefined>(undefined);

  const initializeCamera = useCallback(() => {
    if (!containerRef.current) return null;

    const containerRect = containerRef.current.getBoundingClientRect();
    const aspect = containerRect.width / containerRect.height;
    const width = 10;
    const height = width / aspect;

    const camera = new THREE.OrthographicCamera(width / -2, width / 2, height / 2, height / -2, 0, 100);
    camera.position.set(4, 4, 4);
    camera.lookAt(0, 0, 0);
    cameraRef.current = camera;

    return camera;
  }, [containerRef]);

  const initializeScene = useCallback(() => {
    const scene = new THREE.Scene();
    scene.background = new THREE.Color(gameConfig.background.color);

    sceneRef.current = scene;

    const ambientLight = new THREE.AmbientLight(0xffffff, 0.6);
    scene.add(ambientLight);

    const dirLight = new THREE.DirectionalLight(0xffffff, 0.6);
    dirLight.position.set(10, 20, 0);
    scene.add(dirLight);

    return scene;
  }, [gameConfig.background.color]);

  const updateBackgroundColor = useCallback((color: string | number) => {
    if (sceneRef.current) {
      sceneRef.current.background = new THREE.Color(color);
    }
  }, []);

  const updateCameraAspect = useCallback(() => {
    if (!containerRef.current || !cameraRef.current) return;

    const containerRect = containerRef.current.getBoundingClientRect();
    const aspect = containerRect.width / containerRect.height;
    const width = 10;
    const height = width / aspect;

    cameraRef.current.left = width / -2;
    cameraRef.current.right = width / 2;
    cameraRef.current.top = height / 2;
    cameraRef.current.bottom = height / -2;
    cameraRef.current.updateProjectionMatrix();
  }, [containerRef]);

  const updateCameraPosition = useCallback((height: number, speed: number, timePassed: number) => {
    const camera = cameraRef.current;
    if (camera && camera.position.y < height) {
      camera.position.y += speed * timePassed;
    }
  }, []);

  const resetCamera = useCallback(() => {
    const camera = cameraRef.current;
    if (camera) {
      camera.position.set(4, 4, 4);
      camera.lookAt(0, 0, 0);
    }
  }, []);

  const addMeshToScene = useCallback((mesh: THREE.Mesh) => {
    if (sceneRef.current) {
      sceneRef.current.add(mesh);
    }
  }, []);

  const removeMeshFromScene = useCallback((mesh: THREE.Mesh) => {
    if (sceneRef.current) {
      sceneRef.current.remove(mesh);
    }
  }, []);

  const clearScene = useCallback(() => {
    const scene = sceneRef.current;
    if (scene) {
      const meshes = scene.children.filter((child) => child.type === 'Mesh');
      meshes.forEach((mesh) => scene.remove(mesh));
    }
  }, []);

  const clearAll = useCallback(() => {
    if (sceneRef.current) {
      sceneRef.current.clear();
      sceneRef.current = undefined;
    }
    cameraRef.current = undefined;
  }, []);

  return {
    sceneRef,
    cameraRef,
    initializeCamera,
    initializeScene,
    updateBackgroundColor,
    updateCameraAspect,
    updateCameraPosition,
    resetCamera,
    addMeshToScene,
    removeMeshFromScene,
    clearScene,
    clearAll,
  };
}
